import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { PolarArea } from "react-chartjs-2";
import { Chart as ChartJS, RadialLinearScale, ArcElement, Tooltip, registerables } from "chart.js";
import Icon from "../Icon";
import { faArrowUp, faStar } from "@fortawesome/free-solid-svg-icons";
import { LifePart } from "../../state/LifePart";
import { useData } from "../../storage/accessors";
import { transparentize } from "../../state/helpers";

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, ...registerables);

interface LevelUpModalProps {
    show: boolean;
    onHide: () => void;
    lifePart: LifePart | undefined;
    oldLevel: number;
    newLevel: number;
}

const PRIMARY_COLOR = "rgba(13, 110, 253, 0.8)";
const SUCCESS_COLOR = "#20c997";

export default function LevelUpModal({ show, onHide, lifePart, oldLevel, newLevel }: LevelUpModalProps) {
    const { roles } = useData();
    const [animatedLevel, setAnimatedLevel] = useState(oldLevel);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [chartKey, setChartKey] = useState(0);

    const allParts = Object.values(roles.objects).filter((r): r is LifePart => r != null);

    useEffect(() => {
        if (show) {
            setAnimatedLevel(oldLevel);
            setAnimationComplete(false);
            setChartKey((prev) => prev + 1);

            // Анимация увеличения уровня
            const duration = 1500;
            const steps = 30;
            const stepDuration = duration / steps;
            const increment = (newLevel - oldLevel) / steps;
            let currentStep = 0;

            const timer = setInterval(() => {
                currentStep++;
                if (currentStep >= steps) {
                    setAnimatedLevel(newLevel);
                    setAnimationComplete(true);
                    clearInterval(timer);
                } else {
                    setAnimatedLevel(Math.round(oldLevel + increment * currentStep));
                }
            }, stepDuration);

            return () => clearInterval(timer);
        }
    }, [show, oldLevel, newLevel]);

    // Данные для колеса баланса
    const chartData = {
        labels: allParts.map((p) => p.name),
        datasets: [
            {
                label: "Оценка",
                data: allParts.map((p) => {
                    if (p.uuid === lifePart?.uuid) {
                        return animatedLevel;
                    }
                    return p.startpoint || 0;
                }),
                backgroundColor: allParts.map((p) => {
                    if (p.uuid === lifePart?.uuid) {
                        return animationComplete ? SUCCESS_COLOR : p.color;
                    }
                    return transparentize(p.color, 0.7);
                }),
                borderWidth: allParts.map((p) => p.uuid === lifePart?.uuid ? 3 : 1),
                borderColor: allParts.map((p) => {
                    if (p.uuid === lifePart?.uuid) {
                        return animationComplete ? SUCCESS_COLOR : p.color;
                    }
                    return transparentize(p.color, 0.3);
                }),
            },
        ],
    };

    const chartOptions = {
        scales: {
            r: {
                min: 0,
                max: 10,
                pointLabels: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const index = context.dataIndex;
                        const part = allParts[index];
                        return part ? part.name : '';
                    }
                }
            }
        },
        animation: {
            duration: 300,
        },
    };

    const handleHide = () => {
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleHide}
            centered
            size="lg"
            contentClassName="border-0 shadow-lg"
        >
            <Modal.Header className="border-0 pb-0" closeButton>
                <Modal.Title className="w-100 text-center">
                    <span className="text-success">
                        <Icon icon={faArrowUp} className="me-2" />
                        Повышение уровня!
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-5">
                <div className="d-flex flex-column align-items-center">
                    {/* Название части жизни */}
                    <h4 className="mb-3 text-center" style={{ color: lifePart?.color || PRIMARY_COLOR }}>
                        {lifePart?.name || "Сфера баланса"}
                    </h4>

                    {/* Колесо баланса */}
                    <div
                        key={chartKey}
                        className="mb-4"
                        style={{ width: "100%", maxWidth: "400px", height: "400px" }}
                    >
                        <PolarArea data={chartData} options={chartOptions} />
                    </div>

                    {/* Индикатор повышения */}
                    <div className="d-flex flex-column align-items-center">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Icon icon={faStar} className="text-warning" style={{ fontSize: "1.5rem" }} />
                            <span className="fw-bold" style={{ fontSize: "1.2rem" }}>
                                Уровень {oldLevel} → {newLevel}
                            </span>
                            <Icon icon={faStar} className="text-warning" style={{ fontSize: "1.5rem" }} />
                        </div>
                        <p className="text-muted text-center mb-0">
                            {newLevel === 10
                                ? "Поздравляем! Эта сфера баланса достигла максимального уровня!"
                                : `Вы перешли на уровень ${newLevel}. Так держать!`}
                        </p>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 justify-content-center">
                <Button
                    variant={animationComplete ? "success" : "primary"}
                    size="lg"
                    onClick={handleHide}
                    disabled={!animationComplete}
                >
                    {animationComplete ? (
                        <>
                            <Icon icon={faArrowUp} className="me-2" />
                            Отлично!
                        </>
                    ) : (
                        "Ждём завершения..."
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
